import type { beeTypes } from '@beefree.io/sdk';
import type { JSX } from 'react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IBeeFreeTokenAttributes, Template } from '@feathr/blackbox';
import { Session, TemplateClass } from '@feathr/blackbox';
import type { IEditorIframeProps } from '@feathr/components';
import { Editor, toast } from '@feathr/components';
import { getQuery, useReactionEffect } from '@feathr/hooks';

import { Bee } from '.';
import { createTokenObject, getIsPage } from './BeeEditor.utils';
import type { IBeeEditorConfig } from './defaults';
import { getDefaultTemplateEditorConfig } from './defaults';
import type { IBeeConfig } from './types';

export interface IBeeEditorProps extends Omit<IEditorIframeProps, 'element' | 'src'> {
  config: Omit<IBeeEditorConfig, 'mergefields'>;
  isDevMode?: boolean;
  isReadOnly?: boolean;
  setBeePlugin?: (newPlugin: Bee) => void;
  template: Template;
}

function BeeEditor({
  children,
  config,
  id,
  isLoading,
  setBeePlugin,
  template,
  isReadOnly = false,
  ...editorProps
}: Readonly<IBeeEditorProps>): JSX.Element {
  const [refreshKey, setRefreshKey] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { commentId } = getQuery<{ commentId: string }>();
  const isPage = getIsPage(template.get('_cls'));

  async function handleGetBeeFreeToken(): Promise<IBeeFreeTokenAttributes> {
    try {
      return await Session.getBeeFreeToken(isPage);
    } catch (error) {
      toast(t('Unable to connect to the content editor. Refresh the page to try again.'), {
        type: 'error',
      });
      throw error;
    }
  }

  async function handleTokenRefresh(newBeePlugin: Bee): Promise<void> {
    try {
      const newTokenResponse = await handleGetBeeFreeToken();
      const newToken = createTokenObject(newTokenResponse);
      newBeePlugin.updateToken(newToken);
    } catch (error) {
      toast(
        t('Content editor session expired. Refresh the page to try again. {{- error}}', {
          error,
        }),
        {
          type: 'error',
        },
      );
    }
  }

  function createErrorHandler(newBeePlugin: Bee) {
    return function (error: beeTypes.BeePluginError): void {
      const { code } = error;
      switch (code) {
        case 5101:
          // Token expired and cannot be auto-refreshed
          // Obtain a new token and update the current instance
          handleTokenRefresh(newBeePlugin);
          break;

        case 5102:
          // Complete refresh required - remount entire component
          setRefreshKey((prev) => prev + 1);
          break;

        default:
          toast(t('Content editor encountered an error. Refresh the page to try again.'), {
            type: 'error',
          });
      }
    };
  }

  async function initializeBeePlugin(): Promise<void> {
    const mergefields = template.getMergefields(
      config.models.mergefieldCustomFields.models,
      config.isPinpoint,
      config.isPartnerMessage,
    );
    const type = template.get('_cls');
    const templateParentKind = template.get('parent_kind');
    const templateParent = template.get('parent');

    const beeConfig = getDefaultTemplateEditorConfig({
      ...config,
      // For now, feathr-form is only enabled for LandingPage that are attached to a campaign
      addOns: [
        {
          id: 'feathr-form',
          enabled:
            [TemplateClass.LandingPage].includes(type) &&
            templateParentKind === 'campaign' &&
            !!templateParent,
        },
      ],
      mergefields,
      isReadOnly,
      t,
    });

    const tokenResponse = await handleGetBeeFreeToken();
    const tokenObject = createTokenObject(tokenResponse);

    // Create Bee instance with the BE token
    const newBeePlugin = new Bee(tokenObject);

    // Add error handler after Bee instance is created
    // This should capture any errors from the email editor and reflect them to the user
    beeConfig.onError = createErrorHandler(newBeePlugin);

    const content = JSON.parse(template.get('content').json);
    const templateJSON = { page: content.page, comments: content.comments };

    // Cast from IFeathrBeeConfig to IBeeConfig to appease bad sdk types
    if (commentId) {
      beeConfig.onLoad = function (): void {
        newBeePlugin.showComment(commentId);
      };
    }

    await newBeePlugin.start(beeConfig as IBeeConfig, templateJSON);
    setBeePlugin?.(newBeePlugin);
  }

  const { models } = config;
  useReactionEffect(
    // These must be done fetching before we can render the editor.
    () =>
      !models.fonts.isPending &&
      !models.formCustomFields.isPending &&
      !models.mergefieldCustomFields.isPending,
    () => {
      initializeBeePlugin();
    },
  );

  return (
    <Editor
      {...editorProps}
      element={'div'}
      id={'bee-plugin-container'}
      isLoading={template.isPending || isLoading}
      // Do not remove the key here, it is used to refresh the editor when the token is expired
      key={refreshKey}
      name={'bee-plugin-container'}
      ref={editorRef}
      wrapperId={'editorRoot'}
    >
      {children}
    </Editor>
  );
}

export default BeeEditor;
